import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext, getIn, Formik, Form } from 'formik';
import * as Yup from 'yup';

import { Button } from 'core/components/button';
import Field from 'core/components/form/form.v2';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import { useCurrentState } from 'common/hooks/useCurrentState';
import useStyles from './edit-location-modal.styles';

const EditLocationModal = ({ onClose, index }) => {
  const classes = useStyles();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  // #32207: route-aware product so the max-acres cap (Home/Rental = 40) applies when editing a
  // location on the policy route (the offer store is empty there). State already resolves per-route
  // via useCurrentState.
  const { farmPolicyType } = useFarmPolicyContext();
  const { inPolicyRoute } = useRouteHelper();
  const offerState = useCurrentState(values, { policyType: farmPolicyType, locationIndex: index });

  const mask = getFarmPolicyMask(farmPolicyType, offerState);
  const maxAcres = mask?.maxAcres;

  // #33937: the street address is editable; city/state/zip stay locked (they drive licensing,
  // filed forms and eligibility). On the policy route a street change routes through underwriter
  // approval — the backend flags the endorsement for review when it re-pulls FPC.
  const lockedAddress = getIn(values, `farmLocations[${index}].address`) || {};
  const lockedAddressLine = [[lockedAddress.city, lockedAddress.state].filter(Boolean).join(', '), lockedAddress.zip]
    .filter(Boolean)
    .join(' ');

  const validationSchema = Yup.object().shape({
    address: Yup.string().trim().required('Street address is required'),
    description: Yup.string().required('Description is required'),
    numberOfAcres: Yup.number()
      .required('Acres is required')
      .test('max-acres', `Acreage must not exceed ${maxAcres} for this policy type`, (value) => {
        if (maxAcres == null || value == null) {
          return true;
        }
        return value <= maxAcres;
      })
  });

  const initialValues = {
    address: getIn(values, `farmLocations[${index}].address.address`) || '',
    description: getIn(values, `farmLocations[${index}].description`),
    numberOfAcres: getIn(values, `farmLocations[${index}].numberOfAcres`),
    additionalLegalDescription: getIn(values, `farmLocations[${index}].additionalLegalDescription`) || '',
    locationDirections: getIn(values, `farmLocations[${index}].locationDirections`) || ''
  };

  const handleSubmit = async (modalValues) => {
    setFieldValue(`farmLocations[${index}].address.address`, modalValues.address.trim());
    setFieldTouched(`farmLocations[${index}].address.address`, true);
    setFieldValue(`farmLocations[${index}].description`, modalValues.description);
    setFieldTouched(`farmLocations[${index}].description`, true);
    setFieldValue(`farmLocations[${index}].numberOfAcres`, modalValues.numberOfAcres);
    setFieldTouched(`farmLocations[${index}].numberOfAcres`, true);
    setFieldValue(`farmLocations[${index}].additionalLegalDescription`, modalValues.additionalLegalDescription);
    setFieldTouched(`farmLocations[${index}].additionalLegalDescription`, true);
    setFieldValue(`farmLocations[${index}].locationDirections`, modalValues.locationDirections);
    setFieldTouched(`farmLocations[${index}].locationDirections`, true);
    onClose();
  };

  return (
    <BaseDialog className={classes.modal} onClose={onClose} open>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          Modify Location
        </Label>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit, isValid }) => (
            <Form>
              <Field xs={12} type="string" name="address" id="address" label="Street address" mode="light" />
              {lockedAddressLine && (
                <Label type="infoLabel" className={classes.lockedAddress}>
                  {lockedAddressLine} · city, state and ZIP can’t be changed here
                </Label>
              )}
              {inPolicyRoute && (
                <Label type="infoLabel" className={classes.approvalNote}>
                  Changing the street address requires underwriter approval and re-rates this location.
                </Label>
              )}
              <Field xs={12} type="string" name="description" id="description" label="Location name" mode="light" />
              <Field xs={12} type="numeric" name="numberOfAcres" id="numberOfAcres" label="Acres" mode="light" />
              <Field
                xs={12}
                type="textarea"
                id="additionalLegalDescription"
                name="additionalLegalDescription"
                mode="light"
                label="Additional legal description (optional)"
                rows={3}
              />
              <Field
                xs={12}
                type="textarea"
                id="locationDirections"
                name="locationDirections"
                mode="light"
                label="Location directions (optional)"
                rows={3}
              />
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  onClick={handleSubmit}
                  className={classes.buttons}
                  disabled={!isValid}
                >
                  Save changes
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </BaseDialog>
  );
};

EditLocationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  index: PropTypes.number
};

export default observer(EditLocationModal);
